import { Divider, Space } from "@mantine/core";
import Text from "../Text";
import classes from "./index.module.css";
import { useTranslation } from "@/i18n/client";
import { GeneralEnumeration } from "@/types/enumerations";
import { currency } from "@/utils/formatters";
import { theme } from "@/theme";
import { FilePdf, InfoSecondaryLarge, RadioCheckboxTick } from "../Icons";
import Alert from "../Alert";
import { Trans } from "react-i18next";
import Tooltip from "../Tooltip";
import Button from "../Button";
import { sanitize } from "isomorphic-dompurify";

const rel = "noopener,noreferrer";

interface ProductCardProps {
  title: string;
  depositFrequency?: GeneralEnumeration;
  price?: number;
  priceGreen?: number | null;
  bonuses?: string[];
  description?: string;
  priceListUrl?: string;
  termsLinkUrl?: string;
  active?: boolean;
  fullWidth?: boolean;
  isPopular?: boolean;
  onSelect?: () => void;
}

const ProductCard = ({
  title,
  depositFrequency,
  price,
  priceGreen,
  bonuses = [],
  priceListUrl,
  termsLinkUrl,
  description,
  fullWidth,
  active = false,
  isPopular = false,
  onSelect,
}: ProductCardProps) => {
  const { t } = useTranslation("components");

  const showDepositFrequency = depositFrequency && price;
  const formattedDepositFrequency = depositFrequency?.name.substring(0, depositFrequency.name.indexOf(" ")).toLocaleLowerCase();
  const showDepositFrequencyDescription = showDepositFrequency && depositFrequency.value !== "00";

  return (
    <div className={classes.root} data-active={active} data-full-width={fullWidth} data-popular={isPopular}>
      {isPopular && (
        <div className={classes.popular}>
          <Text variant="reading_medium">{t("ProductCard.popular")}</Text>
        </div>
      )}
      <div className={classes.header}>
        <Text variant="h5">{title}</Text>
      </div>
      <div className={classes.body}>
        {showDepositFrequency ? (
          <div className={classes.priceBox}>
            <Text variant="reading_medium_bold">
              {depositFrequency.value === "00"
                ? t("ProductCard.withoutDepositFrequency")
                : t("ProductCard.depositFrequency", { depositFrequency: formattedDepositFrequency })}
            </Text>
            <Text variant="h3" className={classes.price}>
              {currency(price)}
            </Text>
            {showDepositFrequencyDescription && (
              <Text variant="reading_medium" color="neutral.7">
                {t("ProductCard.depositFrequencyDescription")}
              </Text>
            )}
          </div>
        ) : null}
        {showDepositFrequency && (bonuses.length > 0 || description) ? (
          <div className={classes.divider}>
            <Divider w="100%" color="neutral.3" />
            <div className={classes.dividerText} data-active={active}>
              <Text variant="reading_medium_bold">{t("ProductCard.inAddition")}</Text>
            </div>
          </div>
        ) : null}
        {bonuses.map((text, i) => (
          <div className={classes.bonus} key={i} data-active={active}>
            <Text variant="reading_medium_bold" lh="normal">
              {text}
            </Text>
          </div>
        ))}
        <div className={classes.inner}>
          {description && <ul className={classes.description} dangerouslySetInnerHTML={{ __html: sanitize(description) }} />}
          {!description && priceGreen && bonuses.length > 0 && <Space h="md" />}
          {priceGreen && (
            <Alert
              color={theme.colors?.blue?.[2]}
              icon={<InfoSecondaryLarge width={24} height={24} color={theme.colors?.neutral?.[9]} />}>
              <Text variant="reading" color="neutral.9">
                <Trans i18nKey="ProductCard.priceGreen" t={t} values={{ price: currency(priceGreen) }} />
              </Text>
            </Alert>
          )}
        </div>
        <div className={classes.footer}>
          {priceListUrl && (
            <>
              <Divider w="100%" color="neutral.3" />
              <Button unstyled onClick={() => window.open(priceListUrl, "_blank", rel)} className={classes.link}>
                <FilePdf />
                <Text variant="reading_medium" color="red.5">
                  {t("ProductCard.priceList")}
                </Text>
              </Button>
              {termsLinkUrl && (
                <Button unstyled onClick={() => window.open(termsLinkUrl, "_blank", rel)} className={classes.link}>
                  <FilePdf />
                  <Text variant="reading_medium" color="red.5">
                    {t("ProductCard.termsLink")}
                  </Text>
                </Button>
              )}
            </>
          )}
          {termsLinkUrl && !priceListUrl && (
            <>
              <Divider w="100%" color="neutral.3" />
              <Button unstyled onClick={() => window.open(termsLinkUrl, "_blank", rel)} className={classes.link}>
                <FilePdf />
                <Text variant="reading_medium" color="red.5">
                  {t("ProductCard.termsLink")}
                </Text>
              </Button>
            </>
          )}
          {onSelect && (
            <Tooltip
              hidden
              label={
                <div className={classes.tooltip}>
                  <Text variant="reading_small_bold" color="neutral.8">
                    Dobrá volba!
                  </Text>
                  <Text variant="reading_small">Toto je náš nejoblíbenější tarif. Má ho 70 % našich zákazníků.</Text>
                </div>
              }
              arrowPosition="center">
              <Button
                fullWidth
                mt="2.5rem"
                leftSection={active ? <RadioCheckboxTick /> : null}
                onClick={onSelect}
                data-active={active}>
                {active ? "Vybráno" : "Vybrat"}
              </Button>
            </Tooltip>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
